import { useState, MouseEvent, useMemo, useEffect } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import * as styles from './styles';
import { SortOption } from '@/types';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  PointOfInterestTemplateTypeEnum,
  TemplateTypeEnum,
  usePaintBlockTemplateQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { TOTAL_AREA_FIELD_FORMATTER } from '../../constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';

type Props = {
  onFilterChange?: (checked: boolean) => void;
  onSortChange?: (sortBy: string | undefined, sortDir: number | undefined) => void;
};

const SORTING_ICON_MAP = new Map<undefined | 1 | -1, React.ReactNode>([
  [undefined, <SwapVertIcon />],
  [-1, <ArrowUpwardIcon />],
  [1, <ArrowDownwardIcon />],
]);

const getNextSortDir = (currentSortDir: undefined | 1 | -1): undefined | 1 | -1 => {
  if (currentSortDir === 1) return undefined;
  if (currentSortDir === -1) return 1;
  return -1;
};

export const CustomBlockSortingHeader = ({ onSortChange }: Props) => {
  const structureId = useGetStructureId();

  const { data, loading, error } = usePaintBlockTemplateQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.PaintBlocks,
    },
    // This data has messed up keys so there are unexpected results
    // in the data object, skipping caching fixes it
    fetchPolicy: 'no-cache',
  });

  // Fetch point of interest templates
  const {
    data: poiTemplatesData,
    loading: poiTemplatesLoading,
    error: poiTemplatesError,
  } = useFetchTemplatesByType();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [sortDir, setSortDir] = useState<undefined | 1 | -1>(-1);
  const open = Boolean(anchorEl);

  const sortOptions: SortOption[] = useMemo(() => {
    const paintBlockFields = data?.allTemplates?.at(0)?.fields || [];
    const poiTemplates = poiTemplatesData?.allPointOfInterestTemplate || [];

    // Create sort options from paint block fields
    const paintBlockOptions = paintBlockFields.map((item) => ({
      id: item.name,
      label: item.name,
      type: 'paintBlock' as const,
    }));

    // Create sort options from point of interest templates
    const poiOptions = poiTemplates
      .filter((template) => template.type === PointOfInterestTemplateTypeEnum.PointOfInterest)
      .map((template) => ({
        id: template?.key || template.name,
        label: template.name,
        type: 'poi' as const,
      }));

    return [...paintBlockOptions, ...poiOptions];
  }, [data?.allTemplates, poiTemplatesData?.allPointOfInterestTemplate]);

  //Should Pass field key as sort parameter not field name
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (sortOptions && sortOptions.length > 0) {
      setSortBy(sortOptions[0].id);
    }
  }, [sortOptions]);

  const handleSortClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortingButtonClick = () => {
    setSortDir((previous) => {
      return getNextSortDir(previous);
    });
    onSortChange?.(sortBy, getNextSortDir(sortDir));
  };

  const handleSortOptionSelect = (sortId: string): void => {
    setSortBy(sortId);
    setAnchorEl(null);
    onSortChange?.(sortId, sortDir);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  if (!sortOptions || sortOptions.length === 0) return <></>;

  const currentSortOption = sortOptions.find((option) => option.id === sortBy) || sortOptions[0];

  if (loading || poiTemplatesLoading) return <></>;

  if (error || poiTemplatesError)
    return <p>Error loading templates: {error?.message || poiTemplatesError?.message}</p>;

  return (
    <styles.FilterBar>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <IconButton size="small" onClick={handleSortingButtonClick}>
          {SORTING_ICON_MAP.get(sortDir)}
        </IconButton>
        <styles.SortButton
          sx={{ fontSize: '12px', fontWeight: 400 }}
          onClick={handleSortClick}
          endIcon={<KeyboardArrowDownIcon />}
          aria-controls={open ? 'sort-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {TOTAL_AREA_FIELD_FORMATTER[currentSortOption?.label] || currentSortOption?.label} \ Total
          surface
        </styles.SortButton>

        <Menu
          id="sort-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'sort-button',
          }}
        >
          {sortOptions.map((option) => (
            <MenuItem
              key={option.id}
              onClick={() => handleSortOptionSelect(option.id)}
              selected={option.id === sortBy}
            >
              {TOTAL_AREA_FIELD_FORMATTER[option.label] || option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </styles.FilterBar>
  );
};
